'use client'

import { useTranslations } from 'next-intl'
import { FC, useContext, useEffect, useRef, useState } from 'react'

import { DocKey, docsManifest } from 'src/docs/generated/docsManifest'

import { Button } from '../../basicUI/Button'
import { Flex } from '../../basicUI/Flex'
import { MarkdownToJsx } from '../../basicUI/MarkdownToJsx'
import { Modal } from '../../basicUI/Modal'
import { Text } from '../../basicUI/Text'
import { styled } from '../../stitches.config'
import { fieldDocChatContext } from './fieldDocChatContext'

export interface FieldDocModalProps {
  docKey: DocKey
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const FieldDocModal: FC<FieldDocModalProps> = ({ docKey, open, onOpenChange }) => {
  const t = useTranslations('PLFieldDoc')
  const chatContext = useContext(fieldDocChatContext)
  // In-modal navigation to related concept docs, with a back affordance.
  const [activeKey, setActiveKey] = useState<DocKey>(docKey)
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (open) setActiveKey(docKey)
  }, [open, docKey])

  // Reset the modal's scroll position when navigating between docs.
  useEffect(() => {
    let el = contentRef.current?.parentElement
    while (el) {
      if (el.scrollTop > 0) {
        el.scrollTop = 0
        break
      }
      el = el.parentElement
    }
  }, [activeKey])

  const doc = docsManifest[activeKey]

  const handleAskAi = (prompt: string, autoSend: boolean) => {
    onOpenChange(false)
    chatContext?.askAiAboutDoc({ docKey: activeKey, prompt, autoSend })
  }

  return (
    <Modal open={open} onOpenChange={onOpenChange} title={doc.title} subtitle={doc.summary}>
      <Flex ref={contentRef} direction="column" gap={4} css={{ pb: '$4' }}>
        {activeKey !== docKey && (
          <Button
            label={t('back')}
            variant="ghost"
            iconName="arrow_back"
            onClick={() => setActiveKey(docKey)}
            css={{ alignSelf: 'flex-start' }}
          />
        )}
        <DocBody>
          <MarkdownToJsx markdown={doc.body} />
        </DocBody>
        {doc.concepts.length > 0 && (
          <Flex direction="column" gap={2}>
            <Text variant="overline" css={{ color: '$gs11' }}>
              {t('related')}
            </Text>
            <Flex gap={2} css={{ flexWrap: 'wrap' }}>
              {doc.concepts.map(slug => {
                const conceptKey = `concepts/${slug}` as DocKey
                const conceptDoc = docsManifest[conceptKey]
                if (!conceptDoc) return null
                return (
                  <Button
                    key={slug}
                    label={conceptDoc.title}
                    variant="ghost"
                    iconName="menu_book"
                    onClick={() => setActiveKey(conceptKey)}
                  />
                )
              })}
            </Flex>
          </Flex>
        )}
        {chatContext && (
          <Flex direction="column" gap={2} css={{ borderTop: '1px solid $gs4', pt: '$4' }}>
            <Text variant="overline" css={{ color: '$gs11' }}>
              {t('stillHaveQuestions')}
            </Text>
            <Flex gap={2} css={{ flexWrap: 'wrap' }}>
              {doc.prompts.map(prompt => (
                <Button
                  key={prompt}
                  label={prompt}
                  variant="ghost"
                  iconName="auto_awesome"
                  onClick={() => handleAskAi(prompt, true)}
                />
              ))}
              <Button
                label={t('askAi')}
                variant="tertiary"
                iconName="chat"
                onClick={() => handleAskAi(t('askAiPrefill', { title: doc.title }), false)}
              />
            </Flex>
          </Flex>
        )}
      </Flex>
    </Modal>
  )
}

const DocBody = styled('div', {
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: '$3',
    border: '1px solid $gs4',
  },
})
